import React from "react";
import {ServerError, ServerOfflineError} from "../../../queries/errors";

export const MessageType = {
  None: "",
  AlreadyConfirmed: "AlreadyConfirmed",
  CheckLatestEmail: "CheckLatestEmail",
  ConfirmAccount: "ConfirmAccount",
  ConfirmationFailed: "ConfirmationFailed",
  CopyVerificationCode: "CopyVerificationCode",
  CORSError: "CORSError",
  InvalidUsername: "InvalidUsername",
  LimitExceededException: "LimitExceededException",
  LinkExpired: "LinkExpired",
  LoginError: "LoginError",
  NewConfirmationCode: "NewConfirmationCode",
  NotAuthorized: "NotAuthorized",
  NotAuthorizedException: "NotAuthorizedException",
  PasswordExpired: "PasswordExpired",
  PasswordResetRequiredException: "PasswordResetRequiredException",
  ServiceUnavailable: "ServiceUnavailable",
  UserConfirmed: "UserConfirmed",
  UserLambdaValidationException: "UserLambdaValidationException",
  UserNotConfirmedException: "UserNotConfirmedException",
  UserNotFoundException: "UserNotFoundException",
  VerificationCodeResent: "VerificationCodeResent",
};

export const getMessageObject = (err, arg) => {
  const code = typeof(err) === 'string' ? err : (err.code || err.name);
  const detail = typeof (err) === 'string' ? err : err.message;
  let output = null;

  if (code === MessageType.UserConfirmed) {
    output = {message: `User ${arg ? arg + " " : ""} confirmed, please sign in.`, type: "info",
              name: MessageType.UserConfirmed};
  }
  else if (code === MessageType.PasswordResetRequiredException) {
    output = {message: "You must change your password to continue.", type: "error",
              name: MessageType.PasswordResetRequiredException};
  }
  else if (code === MessageType.PasswordExpired) {
    output = {message: "Your password has expired and must be changed.", type: "error",
              name: MessageType.PasswordExpired};
  }
  // AWS Cognito response: {"__type":"NotAuthorizedException","message":"Incorrect username or password."}
  else if (code === MessageType.UserNotFoundException || code === MessageType.NotAuthorizedException) {
    output = {message: `Sign in failed for user ${arg}\nNew users should select the registration link below`,
              type: "error", name: MessageType.UserNotFoundException};
  }
  else if (code === MessageType.UserNotConfirmedException) {
    // PLEX-1618: Handle undefined email gracefully
    // WEBAPP-3N: Redirect to /verify so user can enter the code; message should reference code, not link
    const email = arg || "your email";
    output = {message: `Please verify your email first. Enter the verification code sent to ${email} to complete registration.`,
              type: "warning", name: MessageType.UserNotConfirmedException};
  }
  else if (code === MessageType.InvalidUsername) {
    output = {message: `Invalid username "${arg}".`, type: "error", name: MessageType.InvalidUsername};
  }
  else if (code === MessageType.LinkExpired) {
    output = {message: "Link expired. Look for a newer confirmation or re-register.", type: "warning",
              name: MessageType.LinkExpired};
  }
  else if (code === MessageType.ConfirmationFailed) {
    output = {message: "Confirmation failed, please re-register.", type: "warning", name: MessageType.ConfirmationFailed};
  }
  else if (code === MessageType.ConfirmAccount) {
    output = {message: `A verification code has been sent to ${arg}.  Enter it here to complete registration.`, type: "info",
              name: MessageType.ConfirmAccount};
  }
  else if (code === MessageType.CheckLatestEmail) {
    output = {message: `Please respond to the latest confirmation email sent to ${arg ? arg : "your email"}.`, type: "warning",
              name: MessageType.CheckLatestEmail};
  }
  else if (code === MessageType.AlreadyConfirmed) {
    // PLEX-1463: Make it clear that the account is already registered and confirmed
    output = {message: "This account is already registered and confirmed. Please sign in below.", type: "warning", name: MessageType.AlreadyConfirmed};
  }
  else if (code === MessageType.NewConfirmationCode) {
    // PLEX-1618: Handle undefined email gracefully
    const email = arg || "your email";
    output = {message: `A new confirmation code has been sent to ${email}. Enter it here to complete registration.`, type: "warning",
              name: MessageType.NewConfirmationCode};
  }
  else if (code === MessageType.NotAuthorized) {
    output = {message: arg ? arg : "User is not authorized to use this application.", type: "error",
              name: MessageType.NotAuthorized};
  }
  else if (code === MessageType.VerificationCodeResent) {
    output = {message: `A new password reset code has been sent to ${arg ? arg : "your email"}. Copy the code from your email and enter it below along with your new password.`, type: "warning",
              name: MessageType.VerificationCodeResent};
  }
  else if (code === MessageType.CopyVerificationCode) {
    output = {message: `A password reset code has been sent to ${arg ? arg : "your email"}. Copy the code from your email and enter it below along with your new password.`, type: "info",
              name: MessageType.CopyVerificationCode};
  }
  else if (code === MessageType.CORSError) {
    output = {message: "CORS policy error: The server is blocking requests from this origin.\nPlease check the server CORS configuration and allowed headers.",
              type: "error", name: MessageType.CORSError};
  }
  else if (code === 'NetworkError') {
    output = {message: "Unable to reach the authentication server (cognito-idp.us-east-1.amazonaws.com). Check your network connection, or ask your IT team to allow access to this address.",
              type: "error", name: 'NetworkError'};
  }
  else if (code === MessageType.ServiceUnavailable) {
    output = {message: "Sign in is temporarily unavailable. Please try again in a moment.", type: "warning",
              name: MessageType.ServiceUnavailable};
  }
  else if (code === MessageType.LoginError) {
    output = {message: `The system can't authorize ${arg ? arg : "this user"}.`, type: "error",
              name: MessageType.LoginError};
  }
  else if (err instanceof ServerOfflineError || err instanceof ServerError) {
    output = {message: "The server is temporarily unavailable. Please try again in a moment.", type: "warning",
              name: MessageType.ServiceUnavailable};
  }
  else {
    // PLEX-2366: Default case for unhandled errors - use "error" type, not username
    output = {message: detail, type: "error", name: code};
  }

  return output;
};

export const Message = ({message = null}) => {
  if (typeof(message) === "string") {
    message = {message, type: MessageType.None, name: ""};
  }
  return (
    <div className={`message ${message?.type}`}>{message?.message || ""}</div>
  );
};